import { Box, Divider, Grid } from '@mui/material'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from '../../store/reducer';
import { APIConstants } from '../utils/api/constants';
import { addLeg, parlayBuilderFiltersMinOdds, removeLeg, resetLegs, setOffers, setOperator, setParlaysData } from './store/slice';
import { getParlaysData } from './store/actions';
import { getISOStringsForTimeframe } from '../pick6/optimizerHelper';
import { IParlayOutcome, Operators, Timeframe } from '../pick6/store/types';
import { getFormattedOffersBeta, legCountAndPickemTypeToOddsDictionary, OperatorsMinOdds } from './store/types';
import Toolbar from './Toolbar'
import WagerPayout from './WagerPayout';
import OutcomesList from './OutcomesList';
import HiddenComponent from './Hidden';
import AdditionalLegs from './AdditionalLegs';
import LegsDrawer from './LegsDrawer';
import DescriptionComponent from './DescriptionComponent';
import { useLocation } from 'react-router-dom';

const ParlayBuilder = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isDrawerOpen, setDrawerOpen] = React.useState<boolean>(false);
  const {
    parlaysData,
    selectedLegs,
    timeZone,
    parlayFilters,
    operator,
    league,
    pickemParlayType,
    state,
  } = useSelector((state: IRootState) => state.pickemOptimizer);

  const authenticated = useSelector((state: IRootState) => state.common.authenticated);

  const isDisplayUnlocked = authenticated.status === APIConstants.SUCCEEDED && authenticated.data.result;

  const handleToggle = () => {
    setDrawerOpen(!isDrawerOpen);
  };

  React.useEffect(() => {
    if (parlaysData.status === APIConstants.SUCCEEDED) {
      dispatch(resetLegs(parlaysData.data));
    }
  }, [parlaysData]);

  const onGenerate = async () => {
    const timeframes = getISOStringsForTimeframe(parlayFilters.timeframe as Timeframe);
    // The backend currently only supports sending an enddate and configures the start date to automatically be roughly "now"
    const data = await getParlaysData(
      { ...parlayFilters, state: state },
      timeframes?.endISOString ?? '',
      isDisplayUnlocked,
    );

    dispatch(setParlaysData(data));
  }

  React.useEffect(() => {
      dispatch(parlayBuilderFiltersMinOdds(OperatorsMinOdds.DEFAULT));
  }, [operator, dispatch])

  React.useEffect(() => {
    const getOffers = async () => {
      const offersRes = await fetch(`https://app-api-odds-shopper-prod.azurewebsites.net/api/leagues/${league}/offers`)
      .then(response => response.json())
      console.log("🚀 ~ getOffers ~ offersRes:", offersRes)
      dispatch(setOffers(getFormattedOffersBeta(offersRes, league, state.abbreviation)))
    }

    getOffers();
  }, [league]);

  const isDisabled = (o: IParlayOutcome): boolean => {
    const odds = legCountAndPickemTypeToOddsDictionary[`${operator}|${pickemParlayType}|${selectedLegs.length - 1}`];
    return odds === undefined;
  };

  React.useEffect(() => {
    if (parlaysData.status === APIConstants.SUCCEEDED) {
      const selectedLegsIds = selectedLegs.map(x => x.outcomeId);
      parlaysData.data.filter(x => selectedLegsIds.indexOf(x.outcomeId) > 1).forEach(x => dispatch(addLeg(x)));
    }
  }, [parlaysData])

  React.useEffect(() => {
    if(location.pathname.includes('underdog')) {
      dispatch(setOperator(Operators.UNDERDOG));
    } else {
      dispatch(setOperator(Operators.PRIZEPICKS));
    }
  }, [])

  return (
    <>
      <Box sx={{
        padding: 0,
        flexGrow: 1,
        marginBottom: '48px',
        '& .divider': {
          width: '100%',
          marginTop: '36px',
        },
        background: '#101010',
      }}>
        <Grid container sx={{
          // padding:{ xs: '8px'},
          background: '#101010',
        }}>
          <Grid item xs={12} sm={12} md={4}>
            <Grid container>
              <Grid item xs={12} paddingTop={1}>
                <Toolbar />
              </Grid>
              <Grid item xs={12}>
                <WagerPayout />
              </Grid>
              <Grid item xs={12}>
                <OutcomesList
                  loading={parlaysData.status === APIConstants.STARTED}
                  selectedLegs={selectedLegs}
                  timeZone={timeZone}
                  isDisplayUnlocked={isDisplayUnlocked}
                  handleRemoveOutcome={(o: IParlayOutcome) => dispatch(removeLeg(o))}
                  handleToggle={handleToggle}
                  isDisabled={isDisabled}
                  onGenerate={onGenerate}
                  operator={operator ?? Operators.NONE}
                  hideUrl={false}
                  state={state.abbreviation}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={8} sx={{
            marginTop: 0,
            paddingLeft: '32px !important',
            padding: { sm: '0px !important', md: '0px 0px 0px 24px !important' },
            '& iframe': {
              maxWidth: '100%',
              maxHeight: '300px',
            },
            '& .MuiBox-root:first-child': {
              paddingBottom: '0px',
            },
            // '& .MuiBox-root': {
            //   padding: '10px 0px',
            // },
          }}>
            <HiddenComponent smDown implementation="js">
              <AdditionalLegs isDisplayUnlocked={isDisplayUnlocked} />
            </HiddenComponent>
          </Grid>
          <Divider className="divider" orientation="horizontal" />
          <Grid item xs={12}>
            <DescriptionComponent />
          </Grid>
        </Grid>
      </Box>
      <LegsDrawer isDrawerOpen={isDrawerOpen} handleToggle={handleToggle}>
        {parlaysData.status === APIConstants.SUCCEEDED && (
          <AdditionalLegs isDisplayUnlocked={isDisplayUnlocked} />
        )}
      </LegsDrawer>
    </>
  )
}

export default ParlayBuilder